import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingBlock, LoadingRows } from '../components/States'
import { StatusPill } from '../components/StatusPill'
import { useAsync } from '../lib/useAsync'
import { rangeLong } from '../lib/format'
import { RoleForm } from './RoleForm'
import type { SaveStatus } from '../types'
import { BackButton } from '../components/BackButton'
import { SectionsView } from '../components/SectionsView'
import { changedFields, editBody, formFromRole, formProblem } from '../lib/role-draft'
import type { RoleFormState } from '../lib/role-draft'
import { sectionsOf, shownSections } from '../lib/sections'

export function RoleDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { me, isAdmin, refresh } = useAuth()
  const toast = useToast()

  const role = useAsync(() => api.getJobDescription(id), [id])
  const versions = useAsync(() => api.getVersions(id), [id])

  const [editing, setEditing] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [draft, setDraft] = useState<RoleFormState | null>(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const jd = role.data

  const startEdit = () => {
    if (!jd) return
    setDraft(formFromRole(jd))
    setReason('')
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setDraft(null)
    setReason('')
  }

  const savePublish = async (status: SaveStatus) => {
    if (!jd || !draft) return
    const problem = formProblem(draft)
    if (problem) {
      toast.fail(problem)
      return
    }
    setSaving(true)
    try {
      const next = await api.updateJobDescription(jd.id, editBody(draft), status)
      role.set(next)
      versions.reload()
      cancelEdit()
      toast.ok(status === 'Published' ? `Published v${next.version}` : `Saved v${next.version} as a draft`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  /** Publishes a draft without opening the form. */
  const publishAsIs = async () => {
    if (!jd) return
    setSaving(true)
    try {
      const next = await api.updateJobDescription(jd.id, editBody(formFromRole(jd)), 'Published')
      role.set(next)
      versions.reload()
      toast.ok(`Published “${next.title}”`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not publish.')
    } finally {
      setSaving(false)
    }
  }

  const sendForApproval = async () => {
    if (!jd || !draft) return
    const changes = changedFields(jd, draft)
    if (!Object.keys(changes).length) {
      toast.fail('Nothing changed yet.')
      return
    }
    if (!reason.trim()) {
      toast.fail('Add a short reason so HR knows why you are asking.')
      return
    }
    setSaving(true)
    try {
      await api.createChangeRequest({
        jdId: jd.id,
        reason,
        changes,
        baseVersion: jd.version,
      })
      cancelEdit()
      await refresh()
      toast.ok('Sent to HR for approval')
      navigate('/job-descriptions?tab=approvals')
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not send.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!jd) return
    setDeleting(true)
    try {
      await api.deleteJobDescription(jd.id)
      await refresh()
      toast.ok(`Deleted “${jd.title}”`)
      navigate('/job-descriptions')
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not delete.')
      setDeleting(false)
      setConfirmingDelete(false)
    }
  }

  /*
   * The role as a PDF, built here from the role on the screen and its history.
   *
   * The history is asked for again rather than taken from the panel below,
   * because the panel may still be loading or may have failed, and a file
   * that quietly left the history out would look complete. The builder is
   * fetched when the button is pressed, not when the role opens.
   */
  const download = async () => {
    if (!jd) return
    setDownloading(true)
    try {
      const [history, { roleBlocks, roleFileName }, { renderPdf }] = await Promise.all([
        api.getVersions(jd.id), import('../lib/role-pdf'), import('../lib/pdf'),
      ])
      const fileName = roleFileName(jd)
      await renderPdf(roleBlocks(jd, history), fileName)
      toast.ok(`Downloaded ${fileName}`)
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'The download did not start.')
    } finally {
      setDownloading(false)
    }
  }

  if (role.error) {
    return (
      <div className="screen screen--narrow" style={{ paddingTop: 28 }}>
        <BackButton to="all job descriptions" onClick={() => navigate('/job-descriptions')} />
        <ErrorState message={role.error} onRetry={role.reload} />
      </div>
    )
  }

  if (role.loading || !jd) {
    return (
      <div className="screen screen--narrow" style={{ paddingTop: 28 }}>
        <LoadingBlock label="Opening this job description…" />
      </div>
    )
  }

  return (
    <div className="screen screen--narrow" style={{ paddingTop: 28 }}>
      <BackButton to="all job descriptions" onClick={() => navigate('/job-descriptions')} />

      {confirmingDelete && jd && (
        <div className="card card--pad confirm" role="alertdialog" aria-labelledby="del-title">
          <h2 id="del-title" className="confirm__title">
            Delete “{jd.title}”?
          </h2>
          <p className="confirm__body">
            This removes the job description and all {jd.version} of its versions. Anyone
            looking at a pay decision made against this role will no longer be able to see the
            text it was based on. It cannot be undone.
          </p>
          <div className="actions">
            <button
              type="button"
              className="btn btn--danger-solid"
              onClick={() => void remove()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      <div className="card card--pad">
        <div className="title-row" style={{ alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: '0 0 10px', fontSize: 26, fontWeight: 600, letterSpacing: '-0.015em' }}>
              {jd.title}
            </h1>
            <div className="detail__pills">
              <span className="pill pill--meta">{jd.family}</span>
              <span className="pill pill--plain">{jd.level}</span>
              <span className="pill pill--plain">{jd.location}</span>
              <StatusPill status={jd.status} />
            </div>
          </div>
          {!editing && (
            <div className="actions">
              {/* A draft is not in the library yet, so the way out of that
                  state is on the screen rather than a side effect of editing. */}
              {isAdmin && jd.status === 'Draft' && (
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  onClick={() => void publishAsIs()}
                  disabled={saving}
                >
                  {saving ? 'Publishing…' : 'Publish'}
                </button>
              )}
              <button type="button" className="btn btn--sm" onClick={startEdit}>
                {isAdmin ? 'Edit in place' : 'Edit and request approval'}
              </button>
              <button type="button" className="btn btn--sm" onClick={() => void download()} disabled={downloading}>
                {downloading ? 'Preparing…' : '↓ Download'}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>

        <dl className="stat-row">
          <div className="stat">
            <dt>Current range</dt>
            <dd>{rangeLong(jd.payMin, jd.payMax, jd.payUnit)}</dd>
          </div>
          <div className="stat">
            <dt>Version</dt>
            <dd>v{jd.version}</dd>
          </div>
          <div className="stat">
            <dt>Last updated</dt>
            <dd>{jd.updated}</dd>
          </div>
          <div className="stat">
            {/* The industry, which is what this has always shown. It was
                labelled "Job family", and the family is the pill at the top,
                so the screen named two different things the same and showed
                neither of them where the label said. */}
            <dt>Industry</dt>
            <dd style={{ fontSize: 15 }}>{jd.industry}</dd>
          </div>
        </dl>
      </div>

      {editing && draft && (
        <div className="card card--pad" style={{ borderColor: '#bfdbfe', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--brand)' }}>
            {isAdmin
              ? `Editing in place. Saving creates v${jd.version + 1}`
              : `Editing ${jd.title}. Changes go to HR for approval`}
          </div>

          {/*
            * Locked for a manager, not for HR.
            *
            * Moving a role between families changes what the role is, so it
            * is not one of the nine fields a manager may propose (FR-17a).
            * HR changing it directly is the intended way, and the hint under
            * the field says so, but it was locked for everybody, so the
            * screen told HR to do a thing it would not let them do.
            */}
          <RoleForm
            draft={draft}
            onChange={setDraft}
            families={me?.settings.jobFamilies ?? []}
            lockFamily={!isAdmin}
          />

          {!isAdmin && (
            <label className="field">
              <span>Why this change? HR sees this with the request</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. The role now owns radar calibration for the Detroit team"
              />
            </label>
          )}

          <div className="actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={saving}
              onClick={() => void (isAdmin ? savePublish('Published') : sendForApproval())}
            >
              {saving ? 'Working…' : isAdmin ? `Publish v${jd.version + 1}` : 'Send for approval'}
            </button>
            {isAdmin && (
              <button
                type="button"
                className="btn"
                disabled={saving}
                onClick={() => void savePublish('Draft')}
              >
                Save as a draft
              </button>
            )}
            <button type="button" className="btn" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            {!isAdmin && <span className="hint">HR reviews every change before it publishes.</span>}
          </div>
        </div>
      )}

      {/*
        The job description itself, section by section under its own
        headings (FR-47). A role read from a document shows the document's
        sections; any other role shows sections built from its fields
        (FR-52), so every role reads the same way and nothing shows twice.
        An empty section at the top that only repeats the document's title
        is left off the screen and nowhere else (FR-68).
      */}
      {!editing && (
        <div className="card card--pad">
          <SectionsView sections={shownSections(sectionsOf(jd), jd.title)} />
        </div>
      )}

      <div className="card card--flush">
        <h2 className="card__head">Version history</h2>
        {versions.loading ? (
          <LoadingRows rows={3} columns={4} />
        ) : versions.error ? (
          <div className="state">
            <p>The history did not load. {versions.error}</p>
            <button type="button" className="btn" onClick={versions.reload}>Try again</button>
          </div>
        ) : (
          (versions.data ?? []).map((h) => (
            <div className="history-row" key={h.version}>
              <b>v{h.version}</b>
              <span>{h.note}</span>
              <em>{h.by}</em>
              <time dateTime={h.date}>{h.date}</time>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
